import React, { ReactNode, useMemo } from "react";
import Heading from "views/shared/typography/Heading";
import Button from "views/shared/buttons/Button";
import EditIcon from "views/shared/Icons/EditIcon";
import ShareIcon from "views/shared/Icons/ShareIcon";
import { trimStringIfToLong } from "utils/stringUtils";
import CommentsCountButtonContainer from "../../../containers/CommentsCountButtonContainer";
import { useTranslation } from "react-i18next";
import { ModelPageViewTypes } from "const/global";
import { ITypedModelType, ModelStatus } from "models/Model";
import useBreakpoint from "views/shared/hook/useBreakpointHook";
import withReportIssueModal from "../../../issue/hoc/withReportIssueModal";
import ReportIssueButton from "../../../layouts/ModelPageLayout/components/ReportIssueButton";
import ModelViewTypeButton from "../../../components/ModelViewTypeButton";
import SkyeTooltip from "../../../../shared/components/SkyeTooltip";
import DownloadAssetsButton from "../../../layouts/ModelPageLayout/components/DownloadAssetsButton";
import { IGlobalStore } from "../../../../../stores/GlobalStore";
import ModelDeleteButton from "../../../containers/DeleteButton";
import { ModelAccessPolicy } from "../../../ModelAccessPolicy";
import useCurrentUser from "../../../../account/hooks/useCurrentUser";
import { IModelIssueReq } from "../../../issue/model/Issue";
import { IssueService } from "../../../issue/service/IssueService";
import ModelDetailsPageGallery from "../Gallery";
import ModelDetailsPageReprocessButton from "./ReprocessButton";

export interface IModelDetailsRowPropTypes {
  GlobalStore?: IGlobalStore;
  model: {
    id: string;
    publicKey: string;
    name: string;
    status: ModelStatus;
    ownerId: number;
    typedModels: { type: ITypedModelType }[];
  };
  view?: ModelPageViewTypes;
  topContent?: ReactNode;
  showCommentsModule?: boolean;
  showEditNameButton?: boolean;
  showShareButton?: boolean;
  showDeleteButton?: boolean;
  showDownloadButton?: boolean;
  onViewChange: (viewType: ModelPageViewTypes) => void;
  onEditName?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
}

const ModelDetailsRow = ({
  onDownload,
  onEditName,
  onShare,
  onViewChange,
  model,
  view,
}: IModelDetailsRowPropTypes) => {
  const currentUserCtx = useCurrentUser();
  const { t } = useTranslation();
  const { breakpoint } = useBreakpoint();
  const breakpointChange = breakpoint("lg");
  const downloadButtonVisible = breakpointChange;
  const canEditModel = ModelAccessPolicy.canEdit(currentUserCtx.user, model);
  const modelFinished = model.status === ModelStatus.Finished;

  const heading = useMemo(
    () => (
      <Heading id="model-name-heading">
        {trimStringIfToLong(model.name, 60)}
      </Heading>
    ),
    [model.name]
  );

  const editBtn = useMemo(
    () => (
      <Button
        startIcon={<EditIcon />}
        className="ml-2"
        type="transparent"
        size="small"
        onClick={() => onEditName()}
      >
        {t("editName")}
      </Button>
    ),
    []
  );

  const viewButton = useMemo(() => {
    return (
      <div className="md:mr-4">
        <ModelViewTypeButton
          disabledViews={{
            [ModelPageViewTypes.Ortho]:
              !modelFinished ||
              !model.typedModels.find(
                (typed) => typed.type === ITypedModelType.Orthophoto
              ),
            [ModelPageViewTypes.Model]: !modelFinished,
          }}
          onClick={onViewChange}
          currentView={view}
        />
      </div>
    );
  }, [modelFinished, model.typedModels, onViewChange, view]);

  const reportButton = withReportIssueModal(ReportIssueButton)({
    model,
    loading: false,
    handleReport: async (issue: IModelIssueReq) =>
      IssueService.reportModel(model.id, issue),
  });

  // Desktop layout
  if (breakpointChange) {
    return (
      <div className="flex flex-col lg:flex-row items-center md:items-stretch justify-between w-full bg-surface-primary p-3 md:p-5 lg:h-[72px] w-full">
        <div className="flex flex-row items-center justify-start">
          {viewButton}
          {heading}
          {canEditModel && editBtn}
        </div>
        <div className="flex flex-row items-center justify-end gap-1">
          <ModelDetailsPageReprocessButton model={model} />
          <ModelDeleteButton model={model} />
          {reportButton}
          {downloadButtonVisible && (
            <DownloadAssetsButton onClick={onDownload} />
          )}
          <SkyeTooltip title={t("share")}>
            <Button
              id="model-share-button"
              className="share-btn right-row-btn"
              endIcon={<ShareIcon />}
              type="light"
              onClick={() => onShare()}
            ></Button>
          </SkyeTooltip>
          <CommentsCountButtonContainer />
          <ModelDetailsPageGallery modelId={model.id} />
        </div>
      </div>
    );
  }

  // Mobile layout
  return (
    <div className="flex flex-col items-center justify-between w-full bg-surface-primary px-3">
      <div className="w-full py-5">{heading}</div>
      <div className="flex flex-row items-center w-full pb-5 gap-2">
        {viewButton}
        <ModelDeleteButton model={model} />
        {downloadButtonVisible && <DownloadAssetsButton onClick={onDownload} />}
        <SkyeTooltip title={t("share")}>
          <Button
            id="model-share-button"
            className="share-btn right-row-btn mr-3"
            endIcon={<ShareIcon />}
            type="light"
            onClick={() => onShare()}
          ></Button>
        </SkyeTooltip>
      </div>
    </div>
  );
};

export default ModelDetailsRow;
